'use strict';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const EXISTING_PROCESSES = [
  'AgCl precipitation',
  'Na2SO3 reduction',
  'Copper cementation',
];

const COUNTRIES = [
  'Argentina','Australia','Austria','Belgium','Bolivia','Brazil','Canada',
  'Chile','China','Colombia','Czech Republic','Denmark','Ecuador','Egypt',
  'Finland','France','Germany','Ghana','Greece','Hungary','India','Indonesia',
  'Ireland','Italy','Japan','Kazakhstan','Malaysia','Mexico','Morocco',
  'Netherlands','New Zealand','Norway','Peru','Philippines','Poland',
  'Portugal','Romania','Russia','Saudi Arabia','Singapore','South Africa',
  'South Korea','Spain','Sweden','Switzerland','Taiwan','Thailand','Turkey',
  'UAE','United Kingdom','United States','Vietnam','Zimbabwe','Other',
];

const COUNTRY_BENCHMARKS = {
  Argentina:        { electricity: 0.06, labor: 7  },
  Australia:        { electricity: 0.10, labor: 38 },
  Austria:          { electricity: 0.14, labor: 30 },
  Belgium:          { electricity: 0.16, labor: 32 },
  Bolivia:          { electricity: 0.06, labor: 4  },
  Brazil:           { electricity: 0.12, labor: 8  },
  Canada:           { electricity: 0.08, labor: 30 },
  Chile:            { electricity: 0.11, labor: 12 },
  China:            { electricity: 0.08, labor: 8  },
  Colombia:         { electricity: 0.09, labor: 5  },
  'Czech Republic': { electricity: 0.13, labor: 15 },
  Denmark:          { electricity: 0.18, labor: 35 },
  Ecuador:          { electricity: 0.07, labor: 4  },
  Egypt:            { electricity: 0.04, labor: 3  },
  Finland:          { electricity: 0.12, labor: 32 },
  France:           { electricity: 0.13, labor: 28 },
  Germany:          { electricity: 0.22, labor: 32 },
  Ghana:            { electricity: 0.08, labor: 3  },
  Greece:           { electricity: 0.14, labor: 18 },
  Hungary:          { electricity: 0.12, labor: 12 },
  India:            { electricity: 0.09, labor: 4  },
  Indonesia:        { electricity: 0.08, labor: 4  },
  Ireland:          { electricity: 0.19, labor: 28 },
  Italy:            { electricity: 0.17, labor: 24 },
  Japan:            { electricity: 0.17, labor: 25 },
  Kazakhstan:       { electricity: 0.05, labor: 6  },
  Malaysia:         { electricity: 0.06, labor: 6  },
  Mexico:           { electricity: 0.09, labor: 6  },
  Morocco:          { electricity: 0.11, labor: 4  },
  Netherlands:      { electricity: 0.16, labor: 30 },
  'New Zealand':    { electricity: 0.10, labor: 28 },
  Norway:           { electricity: 0.06, labor: 40 },
  Peru:             { electricity: 0.07, labor: 5  },
  Philippines:      { electricity: 0.14, labor: 4  },
  Poland:           { electricity: 0.14, labor: 12 },
  Portugal:         { electricity: 0.13, labor: 16 },
  Romania:          { electricity: 0.11, labor: 10 },
  Russia:           { electricity: 0.05, labor: 8  },
  'Saudi Arabia':   { electricity: 0.05, labor: 12 },
  Singapore:        { electricity: 0.14, labor: 22 },
  'South Africa':   { electricity: 0.08, labor: 7  },
  'South Korea':    { electricity: 0.10, labor: 18 },
  Spain:            { electricity: 0.14, labor: 20 },
  Sweden:           { electricity: 0.09, labor: 32 },
  Switzerland:      { electricity: 0.14, labor: 40 },
  Taiwan:           { electricity: 0.09, labor: 15 },
  Thailand:         { electricity: 0.10, labor: 7  },
  Turkey:           { electricity: 0.11, labor: 8  },
  UAE:              { electricity: 0.08, labor: 12 },
  'United Kingdom': { electricity: 0.24, labor: 25 },
  'United States':  { electricity: 0.09, labor: 30 },
  Vietnam:          { electricity: 0.08, labor: 4  },
  Zimbabwe:         { electricity: 0.09, labor: 3  },
  Other:            { electricity: 0.10, labor: 25 },
};

const STEPS = ['Customer', 'Review'];

// ── Shared UI primitives ────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  background: '#fff',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  boxSizing: 'border-box',
};

const focusStyle = {
  outline: 'none',
  borderColor: 'var(--color-blue)',
  boxShadow: '0 0 0 3px rgba(62,104,178,0.15)',
};

function Field({ label, required, hint, children }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.375rem' }}>
        {label}{required && <span style={{ color: '#E53E3E', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {hint && <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder, min, max, step, autoFocus, readOnly }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={e => !readOnly && onChange && onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      autoFocus={autoFocus}
      readOnly={readOnly}
      style={{
        ...inputStyle,
        ...(focused && !readOnly ? focusStyle : {}),
        ...(readOnly ? { background: '#F7F8FA', color: 'var(--color-text-muted)', cursor: 'default' } : {}),
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function Select({ value, onChange, options }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        ...inputStyle,
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23718096' strokeWidth='1.5' fill='none' strokeLinecap='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 0.75rem center',
        paddingRight: '2rem',
        ...(focused ? focusStyle : {}),
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <option value="">— Select —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function TwoCol({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
      {children}
    </div>
  );
}

function ThreeCol({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ margin: '1.5rem 0 1rem', borderBottom: '2px solid var(--color-blue)', paddingBottom: '0.4rem' }}>
      <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--color-dark-blue)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {children}
      </span>
    </div>
  );
}

function UnitInput({ value, onChange, type = 'number', placeholder, min = '0', step, unit, readOnly }) {
  return (
    <div style={{ position: 'relative' }}>
      <Input value={value} onChange={onChange} type={type} min={min} step={step} placeholder={placeholder} readOnly={readOnly} />
      {unit && (
        <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--color-text-muted)', pointerEvents: 'none' }}>
          {unit}
        </span>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function MoebiusBleed() {
  const navigate   = useNavigate();
  const { user }   = useAuth(); // eslint-disable-line no-unused-vars
  const [step, setStep]             = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [pricesBadge, setPricesBadge] = useState(null); // 'live' | 'stale' | 'error'

  const [form, setForm] = useState({
    // Customer
    customer_name:  '',
    contact_person: '',
    country:        '',
    // Block 1 — Feed & Production
    bleed_volume_l_per_day: '',
    existing_process:       '',
    bleed_ag_g_per_l:       '',
    bleed_cu_g_per_l:       '',
    bleed_pd_pt_ppm:        '',
    bleed_pb_g_per_l:       '',
    // Block 2 — Financial & Operational
    silver_price_usd_per_troy_oz:     '84.79',
    gold_price_usd_per_troy_oz:       '4723',
    copper_price_usd_per_tonne:       '14304',
    palladium_price_usd_per_troy_oz:  '980',
    platinum_price_usd_per_troy_oz:   '1050',
    silver_lease_rate_pct_per_year:   '9',
    electricity_cost_usd_per_kwh:   '0.10',
    labor_cost_usd_per_hour:        '25',
    nitric_acid_cost_usd_per_ltr:   '0.50',
    operating_days_per_year:        '250',
    customer_payback_years:         '3',
    // Block 3 — Process Benchmarks
    precip_days:              '2',   // days for precipitation step
    anode_casting_days:       '1',   // days for anode casting
    anode_replacement_days:   '7',   // days for anode replacement
    // total_inventory_days is derived: precip + anode_casting + anode_replacement
    emew_recovery_days:       '1',   // days for emew recovery
    reagent_cost_per_kg_ag:   '0',   // $/kg Ag (reagent cost in precipitation)
    precip_manhours_per_day:  '8',   // operator manhours/day for precipitation
    emew_manhours_per_day:    '1',   // operator manhours/day for emew
    emew_kwh_per_kg_ag:             '5',     // electricity consumption in emew (kWh/kg Ag)
    precip_maintenance_usd_per_year: '10000', // annual maintenance — precipitation system
    emew_maintenance_usd_per_year:   '5000',  // annual maintenance — emew system
    // Review
    upcoming_major_spend_usd: '',
    capex_usd:                '',
    notes:                    '',
  });

  const set = (field) => (val) => setForm(f => ({ ...f, [field]: val }));

  // Derived: total competitor inventory days (read-only, shown in UI)
  const totalInventoryDays =
    (parseFloat(form.precip_days)            || 0) +
    (parseFloat(form.anode_casting_days)     || 0) +
    (parseFloat(form.anode_replacement_days) || 0);

  // Auto-fill regional benchmarks when country changes
  useEffect(() => {
    if (!form.country) return;
    const b = COUNTRY_BENCHMARKS[form.country];
    if (!b) return;
    setForm(f => ({
      ...f,
      electricity_cost_usd_per_kwh: String(b.electricity),
      labor_cost_usd_per_hour:      String(b.labor),
    }));
  }, [form.country]); // eslint-disable-line

  // ── Fetch live metal prices on mount ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    api.get('/metal-prices')
      .then(res => {
        if (cancelled) return;
        const d = res.data?.data || res.data;
        setForm(f => ({
          ...f,
          silver_price_usd_per_troy_oz:    d.silver    != null ? String(d.silver)    : f.silver_price_usd_per_troy_oz,
          gold_price_usd_per_troy_oz:      d.gold      != null ? String(d.gold)      : f.gold_price_usd_per_troy_oz,
          palladium_price_usd_per_troy_oz: d.palladium != null ? String(d.palladium) : f.palladium_price_usd_per_troy_oz,
          platinum_price_usd_per_troy_oz:  d.platinum  != null ? String(d.platinum)  : f.platinum_price_usd_per_troy_oz,
          copper_price_usd_per_tonne:      d.copper    != null ? String(d.copper)    : f.copper_price_usd_per_tonne,
        }));
        setPricesBadge(d.fallback ? 'stale' : d.stale ? 'stale' : 'live');
      })
      .catch(() => { if (!cancelled) setPricesBadge('error'); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  function validateStep() {
    if (step === 0) {
      if (!form.customer_name.trim()) { toast.error('Company name is required'); return false; }
      // Block 1
      if (!form.bleed_volume_l_per_day || isNaN(parseFloat(form.bleed_volume_l_per_day)) || parseFloat(form.bleed_volume_l_per_day) <= 0) {
        toast.error('Bleed volume per day is required'); return false;
      }
      if (!form.existing_process) { toast.error('Existing process is required'); return false; }
      if (form.bleed_ag_g_per_l === '' || isNaN(parseFloat(form.bleed_ag_g_per_l))) {
        toast.error('Bleed Ag (g/L) is required'); return false;
      }
      if (form.bleed_cu_g_per_l === '' || isNaN(parseFloat(form.bleed_cu_g_per_l))) {
        toast.error('Bleed Cu (g/L) is required'); return false;
      }
      // Block 2
      if (!form.silver_price_usd_per_troy_oz   || isNaN(parseFloat(form.silver_price_usd_per_troy_oz)))   { toast.error('Silver Price is required'); return false; }
      if (!form.gold_price_usd_per_troy_oz     || isNaN(parseFloat(form.gold_price_usd_per_troy_oz)))     { toast.error('Gold Price is required'); return false; }
      if (!form.copper_price_usd_per_tonne     || isNaN(parseFloat(form.copper_price_usd_per_tonne)))     { toast.error('Copper Price is required'); return false; }
      if (!form.silver_lease_rate_pct_per_year || isNaN(parseFloat(form.silver_lease_rate_pct_per_year))) { toast.error('Silver Financing / Lease Rate is required'); return false; }
      if (!form.electricity_cost_usd_per_kwh   || isNaN(parseFloat(form.electricity_cost_usd_per_kwh)))   { toast.error('Electricity Cost is required'); return false; }
      if (!form.labor_cost_usd_per_hour        || isNaN(parseFloat(form.labor_cost_usd_per_hour)))        { toast.error('Labor Cost is required'); return false; }
      if (!form.nitric_acid_cost_usd_per_ltr   || isNaN(parseFloat(form.nitric_acid_cost_usd_per_ltr)))   { toast.error('Nitric Acid Cost is required'); return false; }
      if (!form.operating_days_per_year        || isNaN(parseFloat(form.operating_days_per_year)))        { toast.error('Operating Days / Year is required'); return false; }
      if (!form.customer_payback_years         || isNaN(parseFloat(form.customer_payback_years)))         { toast.error('Payback Expectation is required'); return false; }
    }
    return true;
  }

  function next() {
    if (validateStep()) setStep(s => s + 1);
  }

  async function submit() {
    setSubmitting(true);
    try {
      const n = (v) => (v !== '' && !isNaN(parseFloat(v))) ? parseFloat(v) : undefined;
      const payload = {
        app_type:       'moebius_bleed',
        customer_name:  form.customer_name.trim(),
        contact_person: form.contact_person.trim() || undefined,
        country:        form.country || undefined,
        // Block 1
        bleed_volume_l_per_day: parseFloat(form.bleed_volume_l_per_day),
        existing_process:       form.existing_process || undefined,
        bleed_ag_g_per_l:       parseFloat(form.bleed_ag_g_per_l),
        bleed_cu_g_per_l:       parseFloat(form.bleed_cu_g_per_l),
        bleed_pd_pt_ppm:        n(form.bleed_pd_pt_ppm),
        bleed_pb_g_per_l:       n(form.bleed_pb_g_per_l),
        // Block 2
        silver_price_usd_per_troy_oz:     parseFloat(form.silver_price_usd_per_troy_oz),
        gold_price_usd_per_troy_oz:       n(form.gold_price_usd_per_troy_oz),
        copper_price_usd_per_tonne:       n(form.copper_price_usd_per_tonne),
        palladium_price_usd_per_troy_oz:  n(form.palladium_price_usd_per_troy_oz),
        platinum_price_usd_per_troy_oz:   n(form.platinum_price_usd_per_troy_oz),
        silver_lease_rate_pct_per_year:   n(form.silver_lease_rate_pct_per_year),
        electricity_cost_usd_per_kwh:   parseFloat(form.electricity_cost_usd_per_kwh),
        labor_cost_usd_per_hour:        parseFloat(form.labor_cost_usd_per_hour),
        nitric_acid_cost_usd_per_ltr:   parseFloat(form.nitric_acid_cost_usd_per_ltr),
        operating_days_per_year:        parseFloat(form.operating_days_per_year),
        customer_payback_years:         n(form.customer_payback_years),
        // Block 3 — benchmarks passed as formula variables
        precip_days:             parseFloat(form.precip_days)             || 2,
        anode_casting_days:      parseFloat(form.anode_casting_days)      || 1,
        anode_replacement_days:  parseFloat(form.anode_replacement_days)  || 7,
        total_inventory_days:    totalInventoryDays,
        emew_recovery_days:      parseFloat(form.emew_recovery_days)      || 1,
        reagent_cost_per_kg_ag:  parseFloat(form.reagent_cost_per_kg_ag)  || 0,
        precip_manhours_per_day: parseFloat(form.precip_manhours_per_day) || 8,
        emew_manhours_per_day:   parseFloat(form.emew_manhours_per_day)   || 1,
        emew_kwh_per_kg_ag:             parseFloat(form.emew_kwh_per_kg_ag)             || 5,
        precip_maintenance_usd_per_year: parseFloat(form.precip_maintenance_usd_per_year) || 10000,
        emew_maintenance_usd_per_year:   parseFloat(form.emew_maintenance_usd_per_year)   || 5000,
        // Review
        upcoming_major_spend_usd: n(form.upcoming_major_spend_usd),
        capex_usd:                n(form.capex_usd),
        notes:                    form.notes.trim() || undefined,
      };
      const res = await api.post('/assessment', payload);
      navigate(`/assessment/${res.data.data.id}`);
    } catch {
      // error handled by axios interceptor
    } finally {
      setSubmitting(false);
    }
  }

  const stepDotStyle = (i) => ({
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.8125rem', fontWeight: 700,
    background: i < step ? 'var(--color-green)' : i === step ? '#805AD5' : 'var(--color-border)',
    color: i <= step ? '#fff' : 'var(--color-text-muted)',
  });

  return (
    <div style={{ maxWidth: 680, margin: '2rem auto', padding: '0 1rem' }}>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '2rem' }}>
        {STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
              <div style={stepDotStyle(i)}>
                {i < step ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: '0.75rem', fontWeight: i === step ? 600 : 400, color: i === step ? '#5B21B6' : 'var(--color-text-muted)' }}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: i < step ? 'var(--color-green)' : 'var(--color-border)', margin: '0 0.5rem', marginBottom: '1.25rem' }} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="card" style={{ padding: '2rem' }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.5rem' }}>
          <div style={{ width: 6, height: 28, background: '#805AD5', borderRadius: 3, flexShrink: 0 }} />
          <h2 style={{ margin: 0 }}>Moebius Bleed — emew EW Assessment</h2>
        </div>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1.75rem' }}>
          {step === 0 && 'Enter customer details, bleed stream characteristics, economic inputs and process benchmarks.'}
          {step === 1 && 'Review derived metrics and confirm before generating the assessment.'}
        </p>

        {/* ═══════════════════════════════════════════════════════════════════
            Step 0 — Customer + Block 1 + Block 2 + Block 3
        ══════════════════════════════════════════════════════════════════════ */}
        {step === 0 && (
          <>
            {/* ── Customer ──────────────────────────────────────────────── */}
            <Field label="Company Name" required>
              <Input value={form.customer_name} onChange={set('customer_name')} placeholder="e.g. Acme Silver Plating" autoFocus />
            </Field>
            <TwoCol>
              <Field label="Contact Person">
                <Input value={form.contact_person} onChange={set('contact_person')} placeholder="e.g. Jane Smith" />
              </Field>
              <Field label="Country">
                <Select value={form.country} onChange={set('country')} options={COUNTRIES} />
              </Field>
            </TwoCol>

            {/* ── Block 1 ── Feed & Production ──────────────────────────── */}
            <SectionTitle>Block 1 – Feed and Production Inputs</SectionTitle>

            <TwoCol>
              <Field label="Bleed Volume per Day" required hint="Total Moebius bleed stream flow rate">
                <UnitInput value={form.bleed_volume_l_per_day} onChange={set('bleed_volume_l_per_day')} step="1" placeholder="e.g. 500" unit="L/day" />
              </Field>
              <Field label="Existing Process" required hint="Current bleed treatment method being replaced">
                <Select value={form.existing_process} onChange={set('existing_process')} options={EXISTING_PROCESSES} />
              </Field>
            </TwoCol>

            <TwoCol>
              <Field label="Bleed Ag" required hint="Silver concentration in bleed stream">
                <UnitInput value={form.bleed_ag_g_per_l} onChange={set('bleed_ag_g_per_l')} step="0.1" placeholder="e.g. 8.0" unit="g/L" />
              </Field>
              <Field label="Bleed Cu" required hint="Copper concentration in bleed stream">
                <UnitInput value={form.bleed_cu_g_per_l} onChange={set('bleed_cu_g_per_l')} step="0.1" placeholder="e.g. 2.0" unit="g/L" />
              </Field>
            </TwoCol>

            <TwoCol>
              <Field label="Bleed Pd + Pt" hint="Combined palladium and platinum — leave blank if absent">
                <UnitInput value={form.bleed_pd_pt_ppm} onChange={set('bleed_pd_pt_ppm')} step="0.1" placeholder="e.g. 50" unit="ppm" />
              </Field>
              <Field label="Bleed Pb" hint="Lead concentration — leave blank if absent">
                <UnitInput value={form.bleed_pb_g_per_l} onChange={set('bleed_pb_g_per_l')} step="0.01" placeholder="e.g. 0.5" unit="g/L" />
              </Field>
            </TwoCol>

            {/* ── Block 2 ── Financial & Operational ───────────────────── */}
            <SectionTitle>Block 2 – Financial and Operational Inputs</SectionTitle>

            <ThreeCol>
              <Field label="Silver Price" required hint="Source: goldprice.org spot">
                <UnitInput value={form.silver_price_usd_per_troy_oz} onChange={set('silver_price_usd_per_troy_oz')} step="0.01" placeholder="84.79" unit="$/tr oz" />
              </Field>
              <Field label="Gold Price" required hint="Source: goldprice.org spot">
                <UnitInput value={form.gold_price_usd_per_troy_oz} onChange={set('gold_price_usd_per_troy_oz')} step="1" placeholder="4723" unit="$/tr oz" />
              </Field>
              <Field label="Copper Price" required hint="Source: LME cash ($/tonne)">
                <UnitInput value={form.copper_price_usd_per_tonne} onChange={set('copper_price_usd_per_tonne')} step="1" placeholder="14304" unit="$/t" />
              </Field>
            </ThreeCol>

            <ThreeCol>
              <Field label="Silver Financing / Lease Rate" required hint="Annual rate on silver inventory — bank lease rate or cost of capital">
                <UnitInput value={form.silver_lease_rate_pct_per_year} onChange={set('silver_lease_rate_pct_per_year')} step="0.1" placeholder="9" unit="% /yr" />
              </Field>
              <Field
                label="Electricity Cost"
                required
                hint={form.country && COUNTRY_BENCHMARKS[form.country] ? `IEA regional rate for ${form.country}` : 'IEA global industrial avg'}
              >
                <UnitInput value={form.electricity_cost_usd_per_kwh} onChange={set('electricity_cost_usd_per_kwh')} step="0.01" placeholder="0.10" unit="$/kWh" />
              </Field>
              <Field
                label="Labor Cost"
                required
                hint={form.country && COUNTRY_BENCHMARKS[form.country] ? `ILO/World Bank benchmark for ${form.country}` : 'ILO/World Bank global benchmark'}
              >
                <UnitInput value={form.labor_cost_usd_per_hour} onChange={set('labor_cost_usd_per_hour')} step="0.5" placeholder="25" unit="$/hr" />
              </Field>
            </ThreeCol>

            <ThreeCol>
              <Field label="Nitric Acid Cost" required hint="Industrial grade HNO₃ benchmark">
                <UnitInput value={form.nitric_acid_cost_usd_per_ltr} onChange={set('nitric_acid_cost_usd_per_ltr')} step="0.01" placeholder="0.50" unit="$/L" />
              </Field>
              <Field label="Operating Days / Year" required>
                <UnitInput value={form.operating_days_per_year} onChange={set('operating_days_per_year')} step="1" min="1" placeholder="250" unit="days/yr" />
              </Field>
              <Field label="Payback Expectation" required hint="Customer's target payback period">
                <UnitInput value={form.customer_payback_years} onChange={set('customer_payback_years')} step="0.5" placeholder="3" unit="yrs" />
              </Field>
            </ThreeCol>

            <TwoCol>
              <Field label="Palladium Price" hint="Source: LPPM / Johnson Matthey spot">
                <UnitInput value={form.palladium_price_usd_per_troy_oz} onChange={set('palladium_price_usd_per_troy_oz')} step="1" placeholder="980" unit="$/tr oz" />
              </Field>
              <Field label="Platinum Price" hint="Source: LPPM / Johnson Matthey spot">
                <UnitInput value={form.platinum_price_usd_per_troy_oz} onChange={set('platinum_price_usd_per_troy_oz')} step="1" placeholder="1050" unit="$/tr oz" />
              </Field>
            </TwoCol>

            {/* ── Block 3 ── Process Benchmarks ─────────────────────────── */}
            <SectionTitle>Block 3 – Process Benchmarks</SectionTitle>

            {/* Callout: editable defaults */}
            <div style={{ background: '#EBF0FA', border: '1px solid #C3D0EC', borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.8rem', color: 'var(--color-dark-blue)' }}>
              Industry defaults are pre-filled. Override any value to match the customer's actual process.
            </div>

            {/* Inventory / cycle days */}
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
              Silver Inventory Days — Competitor (Precipitation)
            </p>
            <ThreeCol>
              <Field label="Precipitation Step" hint="Time for AgCl or reduction step">
                <UnitInput value={form.precip_days} onChange={set('precip_days')} step="0.5" placeholder="2" unit="days" />
              </Field>
              <Field label="Anode Casting" hint="Time to cast recovered Ag into anodes">
                <UnitInput value={form.anode_casting_days} onChange={set('anode_casting_days')} step="0.5" placeholder="1" unit="days" />
              </Field>
              <Field label="Anode Replacement" hint="Days Ag is locked in anodes before swap">
                <UnitInput value={form.anode_replacement_days} onChange={set('anode_replacement_days')} step="0.5" placeholder="7" unit="days" />
              </Field>
            </ThreeCol>

            <TwoCol>
              <Field label="Total Inventory Days — Competitor" hint="Auto-calculated: sum of the three stages above">
                <UnitInput
                  value={String(totalInventoryDays)}
                  unit="days"
                  readOnly
                />
              </Field>
              <Field label="emew Recovery Days" hint="Ag liberated and recovered within this period">
                <UnitInput value={form.emew_recovery_days} onChange={set('emew_recovery_days')} step="0.5" placeholder="1" unit="days" />
              </Field>
            </TwoCol>

            {/* Labour */}
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0.75rem 0 0.5rem' }}>
              Labour Intensity
            </p>
            <TwoCol>
              <Field label="Manhours / Day — Precipitation" hint="Operator hours per day for precipitation process">
                <UnitInput value={form.precip_manhours_per_day} onChange={set('precip_manhours_per_day')} step="0.5" placeholder="8" unit="hrs/day" />
              </Field>
              <Field label="Manhours / Day — emew" hint="Operator hours per day for emew electrowinning">
                <UnitInput value={form.emew_manhours_per_day} onChange={set('emew_manhours_per_day')} step="0.5" placeholder="1" unit="hrs/day" />
              </Field>
            </TwoCol>

            {/* Energy & Reagents */}
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0.75rem 0 0.5rem' }}>
              Energy &amp; Reagents
            </p>
            <TwoCol>
              <Field label="Electricity — emew" hint="Specific energy consumption per kg Ag recovered">
                <UnitInput value={form.emew_kwh_per_kg_ag} onChange={set('emew_kwh_per_kg_ag')} step="0.1" placeholder="5" unit="kWh/kg Ag" />
              </Field>
              <Field label="Reagent Cost — Precipitation" hint="Combined reagent cost per kg Ag processed (NaOH, reducing agent, etc.) — enter 0 if unknown">
                <UnitInput value={form.reagent_cost_per_kg_ag} onChange={set('reagent_cost_per_kg_ag')} step="0.01" placeholder="0" unit="$/kg Ag" />
              </Field>
            </TwoCol>

            {/* Maintenance */}
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0.75rem 0 0.5rem' }}>
              Maintenance
            </p>
            <TwoCol>
              <Field label="Annual Maintenance — Precipitation" hint="Filter press, pumps, vessels — flat annual cost">
                <UnitInput value={form.precip_maintenance_usd_per_year} onChange={set('precip_maintenance_usd_per_year')} step="500" placeholder="10000" unit="USD/yr" />
              </Field>
              <Field label="Annual Maintenance — emew" hint="emew system annual maintenance — typically lower than precipitation">
                <UnitInput value={form.emew_maintenance_usd_per_year} onChange={set('emew_maintenance_usd_per_year')} step="500" placeholder="5000" unit="USD/yr" />
              </Field>
            </TwoCol>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            Step 1 — Review & Submit
        ══════════════════════════════════════════════════════════════════════ */}
        {step === 1 && (() => {
          // ── Bleed stream quantities ──────────────────────────────────────
          const vol      = parseFloat(form.bleed_volume_l_per_day)            || 0;
          const agGL     = parseFloat(form.bleed_ag_g_per_l)                || 0;
          const cuGL     = parseFloat(form.bleed_cu_g_per_l)                || 0;
          const pdPt     = parseFloat(form.bleed_pd_pt_ppm)                 || 0;
          const opDays   = parseFloat(form.operating_days_per_year)         || 250;
          const agSpot   = parseFloat(form.silver_price_usd_per_troy_oz)    || 0;
          const cuSpot   = parseFloat(form.copper_price_usd_per_tonne)      || 0;
          const pdSpot   = parseFloat(form.palladium_price_usd_per_troy_oz) || 0;
          const ptSpot   = parseFloat(form.platinum_price_usd_per_troy_oz)  || 0;
          const elecRate = parseFloat(form.electricity_cost_usd_per_kwh)    || 0;
          const laborRate= parseFloat(form.labor_cost_usd_per_hour)         || 0;

          // ── Benchmark values ─────────────────────────────────────────────
          const emewKwhPerKg    = parseFloat(form.emew_kwh_per_kg_ag)       || 5;
          const reagentPerKg    = parseFloat(form.reagent_cost_per_kg_ag)   || 0;
          const precipMH        = parseFloat(form.precip_manhours_per_day)  || 8;
          const emewMH          = parseFloat(form.emew_manhours_per_day)    || 1;

          // ── Derived quantities ───────────────────────────────────────────
          const annualVol       = vol * opDays;                             // L/yr
          const agKgPerDay      = vol * agGL / 1000;                        // kg/day
          const agKgPerYear     = agKgPerDay * opDays;                      // kg/yr
          const agTrOzPerYear   = agKgPerYear * 32.1507;                    // tr oz/yr
          const agRevenue       = agTrOzPerYear * agSpot;                   // USD/yr
          const cuKgPerYear     = vol * cuGL / 1000 * opDays;              // kg/yr
          const cuRevenue       = cuKgPerYear * cuSpot / 1000;             // USD/yr
          const pdPtGPerYear    = vol * (pdPt / 1000) * opDays;            // g/yr
          const pdPtKgPerYear   = pdPtGPerYear / 1000;                       // kg/yr
          const pdPtTrOzPerYear = pdPtKgPerYear * 32.1507;                   // tr oz/yr
          const avgPdPtPrice    = (pdSpot + ptSpot) / 2;                     // avg $/tr oz
          const pdPtValueLost   = pdPtTrOzPerYear * avgPdPtPrice;            // USD/yr lost in precipitation cake

          // ── New derived cost metrics ─────────────────────────────────────
          const emewElecCost    = emewKwhPerKg * agKgPerYear * elecRate;    // USD/yr
          const totalReagentCost= reagentPerKg * agKgPerYear;              // USD/yr
          const precipManpowerCost = precipMH * opDays * laborRate;        // USD/yr
          const emewManpowerCost   = emewMH   * opDays * laborRate;        // USD/yr
          const manpowerSaving     = precipManpowerCost - emewManpowerCost; // USD/yr

          const fmt     = (v, d = 0) => v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
          const fmtUSD  = (v, d = 0) => `$${Math.round(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;

          // ── Table sections ───────────────────────────────────────────────
          const bleedRows = [
            { label: 'Annual bleed volume',     value: `${fmt(annualVol)} L/yr`,         formula: 'Vol/day × Operating days' },
            { label: 'Ag flow rate',            value: `${fmt(agKgPerDay, 2)} kg/day`,   formula: 'Vol/day × Ag g/L ÷ 1,000' },
            { label: 'Annual Ag mass',          value: `${fmt(agKgPerYear, 1)} kg/yr`,   formula: 'Ag kg/day × Operating days' },
            { label: 'Annual Ag (troy oz)',     value: `${fmt(agTrOzPerYear)} tr oz/yr`, formula: 'Ag kg/yr × 32.1507' },
            { label: 'Ag value at spot',        value: fmtUSD(agRevenue) + '/yr',        formula: 'Ag tr oz/yr × Silver spot price' },
            { label: 'Annual Cu mass',          value: `${fmt(cuKgPerYear, 1)} kg/yr`,   formula: 'Vol/day × Cu g/L ÷ 1,000 × Op. days' },
            { label: 'Cu value at spot',        value: fmtUSD(cuRevenue) + '/yr',        formula: 'Cu kg/yr × Copper price ÷ 1,000' },
            ...(pdPt > 0 ? [
              { label: 'Annual Pd+Pt mass',                value: `${fmt(pdPtGPerYear, 1)} g/yr`,    formula: 'Vol/day × Pd+Pt ppm ÷ 1,000 × Op. days' },
              { label: 'Annual Pd+Pt (troy oz)',           value: `${fmt(pdPtTrOzPerYear, 2)} tr oz/yr`, formula: 'Pd+Pt kg/yr × 32.1507' },
              { label: 'Pd+Pt value lost in precipitation', value: fmtUSD(pdPtValueLost) + '/yr',   formula: `Pd+Pt tr oz/yr × avg(Pd $${fmt(pdSpot)}, Pt $${fmt(ptSpot)}) per tr oz — co-precipitates into filter cake` },
            ] : []),
          ];

          const costRows = [
            {
              label:   'Electricity cost — emew',
              value:   fmtUSD(emewElecCost) + '/yr',
              formula: `emew kWh/kg Ag (${emewKwhPerKg}) × Ag kg/yr × Electricity rate`,
              tag:     'emew',
            },
            {
              label:   'Reagent cost — precipitation',
              value:   reagentPerKg === 0 ? '—  (enter reagent cost to calculate)' : fmtUSD(totalReagentCost) + '/yr',
              formula: 'Reagent $/kg Ag × Annual Ag kg',
              tag:     'competitor',
            },
            {
              label:   'Manpower cost — precipitation',
              value:   fmtUSD(precipManpowerCost) + '/yr',
              formula: `${precipMH} hrs/day × ${opDays} days × Labor rate`,
              tag:     'competitor',
            },
            {
              label:   'Manpower cost — emew',
              value:   fmtUSD(emewManpowerCost) + '/yr',
              formula: `${emewMH} hr/day × ${opDays} days × Labor rate`,
              tag:     'emew',
            },
            {
              label:   'Manpower saving (precipitation → emew)',
              value:   fmtUSD(manpowerSaving) + '/yr',
              formula: 'Precipitation manpower − emew manpower',
              tag:     'saving',
            },
          ];

          const tagStyle = (tag) => {
            if (tag === 'emew')       return { background: '#EBF0FA', color: 'var(--color-dark-blue)',  border: '1px solid #C3D0EC',  borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap' };
            if (tag === 'competitor') return { background: '#FEF2F2', color: '#B91C1C',                 border: '1px solid #FECACA', borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap' };
            if (tag === 'saving')     return { background: '#F0FDF4', color: '#166534',                 border: '1px solid #BBF7D0', borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap' };
            return {};
          };

          const DerivedTable = ({ rows, hasTags }) => (
            <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: '#EBF0FA' }}>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Parameter</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)', width: 180, whiteSpace: 'nowrap' }}>Value</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Formula / Source</th>
                    {hasTags && <th style={{ width: 90 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.label} style={{ borderTop: '1px solid var(--color-border)', background: i % 2 === 0 ? '#fff' : '#FAFBFD' }}>
                      <td style={{ padding: '0.4rem 0.75rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>{r.label}</td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 700, color: r.tag === 'saving' ? '#166534' : r.tag === 'competitor' ? '#B91C1C' : 'var(--color-blue)', whiteSpace: 'nowrap' }}>
                        {r.value}
                      </td>
                      <td style={{ padding: '0.4rem 0.75rem', color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: '0.775rem' }}>{r.formula}</td>
                      {hasTags && (
                        <td style={{ padding: '0.4rem 0.75rem', textAlign: 'center' }}>
                          {r.tag && <span style={tagStyle(r.tag)}>{r.tag === 'saving' ? '✓ saving' : r.tag}</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

          return (
            <>
              {/* Bleed stream metrics */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                <span style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)' }}>Bleed Stream Metrics</span>
                <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', background: '#F7F8FA', border: '1px solid var(--color-border)', borderRadius: 4, padding: '1px 6px' }}>Read-only — calculated from inputs</span>
              </div>
              <DerivedTable rows={bleedRows} hasTags={false} />

              {/* Cost comparison metrics */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                <span style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)' }}>Annual Cost Comparison</span>
              </div>
              <DerivedTable rows={costRows} hasTags={true} />

              {/* Optional additional inputs */}
              <SectionTitle>Additional Inputs (Optional)</SectionTitle>
              <TwoCol>
                <Field label="Upcoming Major Spend" hint="Planned capex on existing precipitation setup">
                  <UnitInput value={form.upcoming_major_spend_usd} onChange={set('upcoming_major_spend_usd')} step="1000" placeholder="e.g. 30000" unit="USD" />
                </Field>
                <Field label="Estimated System Investment" hint="emew capex — enables payback / ROI calculation">
                  <UnitInput value={form.capex_usd} onChange={set('capex_usd')} step="1000" placeholder="Optional" unit="USD" />
                </Field>
              </TwoCol>
              <Field label="Notes">
                <textarea
                  value={form.notes}
                  onChange={e => set('notes')(e.target.value)}
                  placeholder="Additional context, assumptions, or comments..."
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />
              </Field>

              {/* Assessment summary card */}
              <div style={{ background: '#F5F0FF', border: '1px solid #C4B5E8', borderRadius: 'var(--radius-md)', padding: '1rem', marginTop: '0.5rem' }}>
                <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#5B21B6', marginBottom: '0.5rem' }}>Assessment summary</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 1rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  <span>Company: <strong style={{ color: 'var(--color-text-primary)' }}>{form.customer_name}</strong></span>
                  <span>Country: <strong style={{ color: 'var(--color-text-primary)' }}>{form.country || '—'}</strong></span>
                  <span>Bleed volume: <strong style={{ color: 'var(--color-text-primary)' }}>{form.bleed_volume_l_per_day} L/day</strong></span>
                  <span>Existing process: <strong style={{ color: 'var(--color-text-primary)' }}>{form.existing_process || '—'}</strong></span>
                  <span>Bleed Ag: <strong style={{ color: 'var(--color-text-primary)' }}>{form.bleed_ag_g_per_l} g/L</strong></span>
                  <span>Inventory days (competitor): <strong style={{ color: 'var(--color-text-primary)' }}>{totalInventoryDays} days</strong></span>
                </div>
              </div>
            </>
          );
        })()}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            className="btn btn-outline"
            onClick={() => step === 0 ? navigate('/assessment/new') : setStep(s => s - 1)}
          >
            {step === 0 ? '← Cancel' : '← Back'}
          </button>
          {step < STEPS.length - 1 ? (
            <button className="btn btn-primary" style={{ background: '#805AD5', borderColor: '#805AD5' }} onClick={next}>
              Continue →
            </button>
          ) : (
            <button
              className="btn btn-primary"
              style={{ background: '#805AD5', borderColor: '#805AD5' }}
              onClick={submit}
              disabled={submitting}
            >
              {submitting
                ? <><span className="spinner" style={{ marginRight: 6 }} />Calculating…</>
                : 'Generate Assessment →'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
